import { useEffect } from 'react';
import SEOHead from '../../components/feature/SEOHead';

export default function Membership() {
  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://crownabundance.com';
  
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': 'Membership Tiers | Join The CROWN Abundance Movement',
    'description': 'Discover CROWN Abundance Movement membership options: Open Community, Global Family, and Global Ambassadors. Access transformational curriculum, forums, coaching, and exclusive benefits.',
    'url': `${baseUrl}/membership`,
    'offers': [
      {
        '@type': 'Offer',
        'name': 'Open Community',
        'price': '0',
        'priceCurrency': 'USD',
        'description': 'Free access to foundational community resources'
      },
      {
        '@type': 'Offer',
        'name': 'Global Family',
        'description': 'Full access to transformational content and community with monthly or annual membership'
      },
      {
        '@type': 'Offer',
        'name': 'Global Ambassadors',
        'description': 'Elite leadership circle with exclusive privileges and premium benefits'
      }
    ]
  };

  useEffect(() => {
    document.title = 'Membership Tiers | Join The CROWN Abundance Movement | House of CROWN';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Discover CROWN Abundance Movement membership options: Open Community, Global Family, and Global Ambassadors. Access transformational curriculum, forums, coaching, and exclusive benefits.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'CROWN membership, Global Family membership, Global Ambassadors, Open Community, wealth mastery curriculum, relationship capital, entrepreneurial stewardship');
    }
  }, []);

  const membershipTiers = [
    {
      tier: 'Tier 1',
      name: 'Open Community',
      price: 'Free Access',
      description: 'Start your journey with foundational access to our community',
      features: [
        'Monthly newsletter',
        'Limited webinar access', 
        'Open invitation to public forums'
      ],
      buttonText: 'Join Free',
      popular: false,
      gradient: 'from-slate-600 to-slate-700',
      bgColor: 'bg-slate-50'
    },
    {
      tier: 'Tier 2',
      name: 'Global Family',
      price: 'Monthly/Annual Fee',
      description: 'Full access to transformational content and community',
      features: [
        'Access to all content and charter forums',
        'Member webinars and mentorship',
        'Event discounts'
      ],
      buttonText: 'Join Global Family',
      popular: true,
      gradient: 'from-amber-600 to-yellow-500',
      bgColor: 'bg-amber-50'
    },
    {
      tier: 'Tier 3',
      name: 'Global Ambassadors',
      price: 'Premium + Annual Contribution',
      description: 'Elite leadership circle with exclusive privileges',
      features: [
        'All Tier 2 benefits',
        'Leadership roundtables',
        'Private coaching and early content access',
        'VIP event privileges',
        'Legacy Circle recognition',
        'Option to sponsor scholarships'
      ],
      buttonText: 'Become Ambassador',
      popular: false,
      gradient: 'from-purple-600 to-indigo-600',
      bgColor: 'bg-purple-50'
    }
  ];

  const curriculumTracks = [
    {
      icon: 'ri-book-open-line',
      title: 'Wealth as Dominion',
      description: 'Based on "Money: The Tool for Dominion"',
      formats: ['Masterclasses', 'Self-paced courses', 'Live coaching', 'Panel discussions']
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Relationship Capital',
      description: 'Build emotional intelligence and lasting connections',
      formats: ['Masterclasses', 'Self-paced courses', 'Live coaching', 'Panel discussions']
    },
    {
      icon: 'ri-rocket-line',
      title: 'Entrepreneurial Stewardship',
      description: 'Purpose-driven business leadership and development',
      formats: ['Masterclasses', 'Self-paced courses', 'Live coaching', 'Panel discussions']
    },
    {
      icon: 'ri-compass-line',
      title: 'Purpose and Identity',
      description: 'Discover your calling and align your mission',
      formats: ['Masterclasses', 'Self-paced courses', 'Live coaching', 'Panel discussions']
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Health & Leadership Resilience',
      description: 'Build strength for sustained leadership excellence',
      formats: ['Masterclasses', 'Self-paced courses', 'Live coaching', 'Panel discussions']
    }
  ];

  const valueProposition = [
    {
      icon: 'ri-group-line',
      title: 'Transformational Community',
      description: 'bringing together thought leaders, visionaries, and changemakers'
    },
    {
      icon: 'ri-focus-3-line',
      title: 'Integrated Mastery',
      description: 'of wealth, relationships, purpose, and generational impact'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Faith-Conscious Innovation',
      description: 'blending timeless truths and proven frameworks'
    },
    {
      icon: 'ri-tools-line',
      title: 'Tools and Platforms',
      description: 'including digital courses, mentorship, and summits'
    },
    {
      icon: 'ri-book-mark-line',
      title: 'Authorship-Driven Content',
      description: 'grounded in "Money: The Tool for Dominion"'
    }
  ];

  return (
    <>
      <SEOHead
        title="Membership Tiers | Join The CROWN Abundance Movement | House of CROWN"
        description="Discover CROWN Abundance Movement membership options: Open Community, Global Family, and Global Ambassadors. Access transformational curriculum, forums, coaching, and exclusive benefits."
        keywords="CROWN membership, Global Family membership, Global Ambassadors, Open Community, wealth mastery curriculum, relationship capital, entrepreneurial stewardship"
        canonicalPath="/membership"
        type="WebPage"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen" style={{ background: '#3D2C8D' }}>
        {/* Hero Section */}
        <section className="relative py-32 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-amber-400/20 to-yellow-300/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-amber-500/15 to-yellow-400/15 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute bottom-32 left-1/3 w-40 h-40 bg-gradient-to-r from-amber-300/10 to-yellow-200/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="text-5xl lg:text-7xl font-bold mb-8" style={{ fontFamily: '"Playfair Display", serif', color: '#F5A623' }}>
              Membership
              <span className="block text-6xl lg:text-8xl bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">
                Structure
              </span>
            </h1>
            
            <p className="text-xl max-w-4xl mx-auto leading-relaxed mb-12" style={{ color: '#FFFFFF' }}>
              Choose your level of engagement and access transformational curriculum designed for wealth, relationships, and legacy mastery.
            </p>
          </div>
        </section>

        {/* Value Proposition Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: '"Playfair Display", serif', color: '#F5A623' }}>
                Value Proposition
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto rounded-full mb-8"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {valueProposition.map((value, index) => (
                <div 
                  key={index}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-amber-400/20 hover:bg-white/10 transition-all duration-300 transform hover:-translate-y-2"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-xl flex items-center justify-center mb-6">
                    <i className={`${value.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold mb-4" style={{ color: '#F5A623' }}>
                    {value.title}
                  </h3>
                  <p className="leading-relaxed" style={{ color: '#FFFFFF' }}>
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Membership Tiers */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: '"Playfair Display", serif', color: '#F5A623' }}>
                Membership Tiers
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto rounded-full mb-8"></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {membershipTiers.map((tier, index) => (
                <div key={index} className={`relative bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden border border-amber-400/20 ${tier.popular ? 'ring-2 ring-amber-500 scale-105' : ''}`}>
                  {tier.popular && (
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-amber-600 to-yellow-500 text-white text-center py-2 text-sm font-medium">
                      Most Popular
                    </div>
                  )}
                  
                  <div className="p-8">
                    <div className="text-center mb-6">
                      <div className="text-sm font-medium mb-2" style={{ color: '#F5A623' }}>{tier.tier}</div>
                      <div className={`w-16 h-16 bg-gradient-to-r ${tier.gradient} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                        <i className="ri-crown-line text-2xl text-white"></i>
                      </div>
                    </div>
                    
                    <h3 className="text-2xl font-bold mb-2 text-center" style={{ color: '#F5A623' }}>{tier.name}</h3>
                    <div className="text-lg font-bold mb-2 text-center" style={{ color: '#FFFFFF' }}>
                      {tier.price}
                    </div>
                    <p className="text-sm mb-6 text-center" style={{ color: '#FFFFFF' }}>{tier.description}</p>
                    
                    <ul className="space-y-3 mb-8">
                      {tier.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start">
                          <i className="ri-check-line mr-3 mt-1 flex-shrink-0" style={{ color: '#F5A623' }}></i>
                          <span className="text-sm" style={{ color: '#FFFFFF' }}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <button className={`w-full py-3 rounded-lg font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      tier.popular 
                        ? 'bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white' 
                        : 'bg-white/20 hover:bg-white/30 text-white border border-amber-400/50'
                    }`}>
                      {tier.buttonText}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Curriculum Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: '"Playfair Display", serif', color: '#F5A623' }}>
                Curriculum & Learning Tracks
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-400 to-amber-600 mx-auto rounded-full mb-8"></div>
              <p className="text-lg max-w-2xl mx-auto" style={{ color: '#FFFFFF' }}>
                Comprehensive learning delivered through multiple formats for maximum impact and flexibility.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {curriculumTracks.map((track, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-amber-400/20 hover:bg-white/10 transition-all duration-300">
                  <div className="w-12 h-12 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${track.icon} text-xl text-white`}></i>
                  </div>
                  <h4 className="text-xl font-bold mb-3" style={{ color: '#F5A623' }}>{track.title}</h4>
                  <p className="text-sm mb-4 leading-relaxed" style={{ color: '#FFFFFF' }}>{track.description}</p>
                  <div className="text-xs" style={{ color: '#F5A623' }}>
                    Formats: {track.formats.join(', ')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <div className="bg-gradient-to-r from-amber-600 to-yellow-500 rounded-3xl p-12 text-white">
              <h3 className="text-3xl lg:text-4xl font-bold mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
                Ready to Master Your Crown?
              </h3>
              <p className="text-xl mb-8 text-white/90">
                Join thousands of leaders transforming their wealth, relationships, and legacy through The CROWN Abundance Movement.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-white text-amber-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap">
                  <i className="ri-crown-line mr-2"></i>
                  Start Your Journey
                </button>
                <button className="border-2 border-white text-white hover:bg-white hover:text-amber-600 px-8 py-4 rounded-lg font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap">
                  <i className="ri-calendar-line mr-2"></i>
                  Schedule Consultation
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
